import { useSelector } from 'react-redux';
import { FormInput } from '~/components/FormComponents/FormInput.js';
import { useGeneralInfoLegalResidentForm } from './hooks/useGeneralInfoLegalResidentForm.ts';
import { FormProvider } from 'react-hook-form';
import { Box, Button, Grid, Typography } from '@mui/material';
import { AddressInfo } from '../../components/FormComponents/AddressInfo.js';
import { PhoneList } from '../../components/FormComponents/PhoneList.tsx';
import { EmailList } from '../../components/FormComponents/EmailList.tsx';
import { AdditionQuestionnaire } from './components/AdditionQuestionnaire/AdditionQuestionnaire.tsx';
import { TGeneralInfoLegalResident } from './types.ts';
import { errorMessages } from '~/constants/errorMessages.js';
import {
  updateForm,
  updateFormFatcaAdditionalInfo,
} from '~/store/identification-form/thunks/thunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  setIdentificationError,
  updateFormData,
  updateFormDataBack,
} from '~/store/identification-form/slices.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { FormPhoneField } from '~/components/FormComponents/FormPhoneField.tsx';
import { TIdentificationForm } from '~/store/identification-form/types.ts';
import { InformationMessageBlock } from '~/modules/identification/components/InformationMessage/InformationMessageBlock.tsx';
import { infoMessages } from '~/constants/infoMessages.ts';
import { trimValues } from '~/modules/identification/utils.ts';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { styles } from '~/modules/identification/styles.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext.tsx';
import { testDataGeneralInfoLegalResident } from '~/modules/identification/forms/GeneralInfo/constants.ts';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion.tsx';
import {
  informerFATCA,
  informerIsControllingPersonTaxResidentOtherJurisdiction,
  informerIsOtherTaxResident,
} from '~/modules/identification/forms/GettingStarted/constants.tsx';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import {
  selectApplicationId,
  selectIsReidentification,
} from '~/store/identification-form/selectors.ts';
import {
  taxResidencyAdditionalInfoDelete,
  taxResidencyAdditionalInfoPrefill,
} from '~/store/identification-form/thunks/additionalInformationThunks.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';

interface IGeneralInfoLegalResident {
  onNextStep: () => void;
}

export const GeneralInfoLegalResident = ({ onNextStep }: IGeneralInfoLegalResident) => {
  const dispatch = useAppDispatch();
  const form = useGeneralInfoLegalResidentForm();
  const { isDirty, isSubmitting } = form.formState;
  const appId = useSelector(selectApplicationId);
  const isReidentification = useSelector(selectIsReidentification);

  const {
    isTestEnvironmentEnabled,
    isAdditionalInformationAboutTaxResidencyEnabled,
    isFATCALegalResidentEnabled,
  } = useFeatureFlags();

  const handleFillTestData = () =>
    form.reset(testDataGeneralInfoLegalResident, { keepDefaultValues: true });

  const onSubmit =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TGeneralInfoLegalResident) => {
      const generalInfoValues = {
        ...values,
        employeeCount: Number(values.employeeCount),
      };

      if (isDirty) {
        try {
          const contactNumbers = values.contactNumbers.map((phone) => {
            const phoneNumber = phone?.replace(/\D/g, '') ?? '';
            return phoneNumber.length < 4 ? null : phone;
          });

          const generalInfo = {
            ...values,
            contactNumbers: contactNumbers.every((phone) => phone === null) ? null : contactNumbers,
          };

          const data = {
            generalInfo: trimValues(generalInfo),
            applicationId: appId as number,
          };
          let callbackFunc;
          const isTaxResidencyAdditionalInformation =
            generalInfoValues.isOtherTaxResident ||
            generalInfoValues.isControllingPersonTaxResidentOtherJurisdiction;

          if (isAdditionalInformationAboutTaxResidencyEnabled) {
            if (isTaxResidencyAdditionalInformation) {
              callbackFunc = async () => {
                await dispatch(
                  taxResidencyAdditionalInfoPrefill({
                    applicationId: appId!,
                    formId: IdentificationFormSteps.generalInfo,
                  })
                );
              };
            } else {
              callbackFunc = async () => {
                await dispatch(
                  taxResidencyAdditionalInfoDelete({
                    applicationId: appId!,
                  })
                );
              };
            }
          }
          await dispatch(
            updateForm({
              data,
              reportId: IdentificationFormSteps.generalInfo,
              callbackFunc,
            })
          );

          if (isFATCALegalResidentEnabled) {
            await dispatch(updateFormFatcaAdditionalInfo(appId as number)).unwrap();
          }

          const formData = {
            field: IdentificationFormStepNames.generalInfo as keyof TIdentificationForm,
            data: generalInfoValues,
          };

          dispatch(updateFormData(formData));
          dispatch(updateFormDataBack(formData));
          !disableGoNext && onNextStep();
        } catch (error) {
          console.error(error);
          dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
        }
      } else {
        !disableGoNext && onNextStep();
      }
    };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(onSubmit(isDirty), onInvalidForm)}>
        {isTestEnvironmentEnabled && (
          <Box sx={styles.testEnvironmentButton}>
            <Button variant={'outlined'} onClick={handleFillTestData}>
              Заповнити даними
            </Button>
          </Box>
        )}
        <Grid container spacing={3}>
          {isReidentification && (
            <InformationMessageBlock informationText={infoMessages.RE_VERIFICATION_MESSAGE} />
          )}
          <FormGridItem>
            <FormInput
              name="fullNameUkr"
              label="Повне найменування (українською)"
              placeholder="Введіть повне найменування (українською)"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="fullNameEng"
              label="Повне найменування (англійською)"
              placeholder="Введіть повне найменування (англійською)"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="shortNameUkr"
              label="Скорочене найменування українською (за наявності)"
              placeholder="Введіть скорочене найменування українською"
              fullWidth
            />
          </FormGridItem>
          <FormGridItem>
            <FormInput
              name="employeeCount"
              inputProps={{ maxLength: 7, inputMode: 'numeric' }}
              label="Кількість штатних працівників"
              placeholder="Введіть кількість штатних працівників"
              fullWidth
            />
          </FormGridItem>
          <AddressInfo showAddressCode />
          <FormGridItem sx={{ ...commonStyles.centerFlexContainer, gap: 1 }}>
            <FormPhoneField name={`financialPhone`} label="Фінансовий номер телефону" />
          </FormGridItem>
          <PhoneList />
          <EmailList />
          <AdditionQuestionnaire />
          {isAdditionalInformationAboutTaxResidencyEnabled && (
            <>
              <Typography variant="h5" sx={{ textAlign: 'center' }}>
                Інформація щодо ознак податкового резидента іншої юрисдикції (країни) крім України
                та США
              </Typography>
              <FormYesNoQuestion
                question={
                  'Чи є Ви податковим резидентом іншої юрисдикції (країни) крім України та США?'
                }
                tooltipTitle={informerIsOtherTaxResident}
                name="isOtherTaxResident"
              />
              <FormYesNoQuestion
                question={
                  'Чи є Ваші Підзвітні особи (КБВ, засновники) резидентами іншої юрисдикції (країни) крім України та США?'
                }
                tooltipTitle={informerIsControllingPersonTaxResidentOtherJurisdiction}
                name="isControllingPersonTaxResidentOtherJurisdiction"
              />
            </>
          )}
          {isFATCALegalResidentEnabled && (
            <>
              <Typography variant="h5" sx={{ textAlign: 'center' }}>
                Інформація щодо приналежності до податкового резидентства США (FATCA)
              </Typography>
              <FormYesNoQuestion
                question={
                  'Чи є Ваша установа податковим резидентом США та/або заснована в США, має адресу реєстрації в США?'
                }
                tooltipTitle={informerFATCA}
                name="isUSTaxResident"
              />
              <FormYesNoQuestion
                question={
                  'Чи є Ваша установа іноземною фінансовою установою відповідно до Закону FATCA («іноземна фінансова установа» означає будь-яку фінансову установу, крім фінансових установ США)?'
                }
                tooltipTitle={informerFATCA}
                name="isForeignFinancialUnderFATCA"
              />
              <FormYesNoQuestion
                question={
                  'Чи є контролююча особа та/або власник істотної участі (10% і більше) Вашої установи громадянином США або податковим резидентом США відповідно до законодавства США?'
                }
                tooltipTitle={informerFATCA}
                name="isControllingPersonUSTaxResident"
              />
            </>
          )}
          <Grid size={12} sx={{ justifyContent: 'center', display: 'flex' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              Продовжити
            </Button>
          </Grid>
        </Grid>
        <SetDirtyModalValues
          saveValues={form.handleSubmit(onSubmit(isDirty, true), onInvalidForm)}
        />
        {isSubmitting && <FullPageLoader />}
      </Box>
    </FormProvider>
  );
};
